/**
 * CommissionOrderPanel — Aug-2026 (User-Wunsch Jörg).
 * Auf dem Lieferschein wurde ein KOMMISSIONS-/KUNDENVERMERK erkannt
 * (Kundenname / Telefonnummer / Auftragsnummer). Passende Werkstatt-Aufträge
 * werden mit Details vorgeschlagen — pro Artikel-Zeile kann in der Tabelle
 * (Spalte „Auftrag") Zubuchen/Ablehnen gewählt werden; 1 Klick ordnet die
 * gewählten Artikel dem Auftrag als Positionen zu (inkl. Bestands-Abgang).
 */
import { useState } from "react";
import { Loader2, UserSearch, Wrench, XCircle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUS_LABEL = {
    new: "Neu", open: "Offen", in_progress: "In Arbeit", waiting_parts: "Wartet auf Teile",
    scheduled: "Geplant", ready: "Fertig zur Abholung", paused: "Pausiert",
};

export const CommissionOrderPanel = ({ scan, rows = [], onRejectAll, onAcceptAll }) => {
    const cm = scan.commission_order_match || {};
    const orders = cm.orders || [];
    const [orderId, setOrderId] = useState(orders[0]?.work_order_id || "");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const [rejected, setRejected] = useState(false);

    const noteParts = [
        cm.note?.customer_name && `Kunde „${cm.note.customer_name}"`,
        cm.note?.phone && `Tel. ${cm.note.phone}`,
        cm.note?.order_number && `Auftrag ${cm.note.order_number}`,
    ].filter(Boolean).join(" · ");

    const selected = orders.find((o) => o.work_order_id === orderId) || null;
    const activeRows = rows.filter((r) => r.toOrder !== false && r.action !== "skip" && Number(r.quantity) > 0 && !r._backorder);
    const totalRows = rows.filter((r) => r.action !== "skip" && Number(r.quantity) > 0 && !r._backorder);

    const assign = async () => {
        if (!orderId) { toast.error("Bitte Auftrag wählen"); return; }
        if (activeRows.length === 0) { toast.error("Keine Position auf ‚Zubuchen' gesetzt (Spalte ‚Auftrag' in der Tabelle)"); return; }
        setBusy(true);
        try {
            const items = activeRows.map((r) => ({
                name: r.name, sku: r.sku || null,
                quantity: Number(r.quantity) || 1,
                purchase_price_net: Number(r.purchase_price_net) || 0,
            }));
            const r = await api.post("/wms/warranty/assign-parts", {
                work_order_id: orderId, items, billing_category: "standard",
                note: `Kommissionsvermerk auf Lieferschein${noteParts ? ` (${noteParts})` : ""}`,
            });
            setDone(r.data);
            toast.success(`${r.data.added} Artikel dem Auftrag ${r.data.order_number} zugeordnet`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuordnen fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const reject = () => {
        setRejected(true);
        onRejectAll?.();
        toast.info("Kommissionsvermerk abgelehnt — Artikel werden normal ins Lager gebucht");
    };
    const undoReject = () => {
        setRejected(false);
        onAcceptAll?.();
    };

    return (
        <div className="mt-1.5 border border-violet-300 bg-violet-50 rounded-sm px-2.5 py-2" data-testid="scan-commission-order-panel">
            <p className="text-[11px] font-bold text-violet-900 inline-flex items-center gap-1.5 flex-wrap">
                <UserSearch className="w-3.5 h-3.5 shrink-0" />
                Kommissionsvermerk erkannt{noteParts ? `: ${noteParts}` : ""}
                {cm.matched_by ? ` — Treffer über ${cm.matched_by}` : ""}
                {cm.customer?.name ? ` (Kunde: ${cm.customer.name})` : ""}
            </p>
            {done ? (
                <p className="text-[11px] font-bold text-violet-800 mt-1" data-testid="scan-commission-order-done">
                    ✓ {done.added} Artikel auf Auftrag {done.order_number} zugeordnet.
                </p>
            ) : rejected ? (
                <p className="text-[11px] text-violet-800 mt-1 inline-flex items-center gap-2" data-testid="scan-commission-order-rejected">
                    Vermerk abgelehnt — keine Zuordnung zum Auftrag.
                    <button type="button" onClick={undoReject} className="underline font-bold inline-flex items-center gap-1" data-testid="scan-commission-order-undo">
                        <Undo2 className="w-3 h-3" /> Rückgängig
                    </button>
                </p>
            ) : orders.length === 0 ? (
                <p className="text-[11px] text-violet-800 mt-1" data-testid="scan-commission-order-none">
                    Kein offener Auftrag zum Vermerk gefunden — bitte manuell prüfen.
                </p>
            ) : (
                <>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        <select value={orderId} onChange={(e) => setOrderId(e.target.value)}
                            className="h-8 px-2 rounded-sm border border-violet-300 text-xs bg-white"
                            data-testid="scan-commission-order-select">
                            {orders.map((o) => (
                                <option key={o.work_order_id} value={o.work_order_id}>
                                    {o.order_number}{o.customer_name ? ` · ${o.customer_name}` : ""}{o.vehicle ? ` · ${o.vehicle}` : ""}
                                </option>
                            ))}
                        </select>
                        <button type="button" onClick={assign} disabled={busy || activeRows.length === 0}
                            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white bg-violet-700 hover:bg-violet-800 rounded-sm px-3 py-2 disabled:opacity-60"
                            data-testid="scan-commission-order-assign-btn">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />}
                            {activeRows.length} Artikel diesem Auftrag zubuchen
                        </button>
                        <button type="button" onClick={reject}
                            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-red-700 border border-red-300 bg-white hover:bg-red-50 rounded-sm px-3 py-2"
                            data-testid="scan-commission-order-reject-btn">
                            <XCircle className="w-3.5 h-3.5" /> Ablehnen
                        </button>
                    </div>
                    {selected && (
                        <div className="mt-1.5 bg-white border border-violet-200 rounded-sm px-2.5 py-1.5 text-[11px] text-violet-950 flex flex-wrap gap-x-4 gap-y-0.5" data-testid="scan-commission-order-details">
                            <span><b>Auftrag:</b> {selected.order_number}</span>
                            <span><b>Status:</b> {STATUS_LABEL[selected.status] || selected.status || "—"}</span>
                            <span><b>Angelegt:</b> {selected.created_at || "—"}</span>
                            {selected.customer_name && <span><b>Kunde:</b> {selected.customer_name}</span>}
                            {selected.vehicle && <span><b>Fahrzeug:</b> {selected.vehicle}</span>}
                            {selected.license_plate && <span><b>Kennzeichen:</b> {selected.license_plate}</span>}
                            {selected.title && <span className="w-full text-violet-700 truncate"><b>Arbeit:</b> {selected.title}</span>}
                        </div>
                    )}
                    <p className="text-[10px] text-violet-700 mt-1" data-testid="scan-commission-order-count">
                        {activeRows.length} von {totalRows.length} Position(en) auf „Zubuchen" — Auswahl pro Zeile in der Tabelle unten (letzte Spalte „Auftrag").
                    </p>
                </>
            )}
        </div>
    );
};
